import React from 'react';
import {withTranslation} from 'react-i18next';
import {Field, Form as FinalForm} from 'react-final-form';
import {Button, Col, Form, Input, Row} from 'reactstrap';
import TextField from '../components/TextField';
import LoaderButton from '../components/LoaderButton';
import ModalManager from '../components/modal/ModalManager';
import {NotificationManager} from "../components/common/react-notifications";
import {dataURItoBlob, difference, numberWithCommas, stringToNumber} from "../helpers/Utils";
import {composeValidators, mustBeInteger, mustBeNumber, required} from "../helpers/Validators";
import FieldError from "../components/FieldError";
import FileField from "../components/FileField";
import {find, forEach, isEqual} from 'lodash';
import Switch from 'react-switch';
import Api from "../services/Api";

class PairManageForm extends React.Component {
  addPair = async values => {
    const {t, afterSubmit} = this.props;
    try {
      const {name, faName, enName, priceDecimals, tradeAmountDecimals, active, logoUrl, baseToken, quoteToken} = values;
      const formData = new FormData();
      formData.append("name", name);
      formData.append("baseToken", baseToken);
      formData.append("quoteToken", quoteToken);
      formData.append("tradeAmountDecimals", tradeAmountDecimals);
      formData.append("priceDecimals", priceDecimals);
      formData.append("labels", JSON.stringify([
        {locale: "en", value: enName},
        {locale: "fa", value: faName}
      ]));
      formData.append("active", String(active));
      if (logoUrl) {
        formData.append("image", dataURItoBlob(logoUrl));
      }
      await Api.invoke(
        'POST',
        '/v2/admin-ui/pairs',
        formData
      );
      NotificationManager.success(t('Pair {{name}} added successfully', {name}), t('Message'));
      afterSubmit();
      ModalManager.close({
        isOpen: false
      });
    } catch (e) {
      if (e.fieldError) {
        return e.fieldError;
      }
      throw e;
    }
  };

  updatePair = async values => {
    const {t, pair, afterSubmit} = this.props;
    const data = {
      tradeAmountDecimals: Number(values.tradeAmountDecimals),
      priceDecimals: Number(values.priceDecimals),
      active: values.active,
      logoUrl: values.logoUrl,
    };
    const diff = difference(data, pair);
    const newValues = {
      ...diff,
      labels:  [
        {locale: "en", value: values.enName},
        {locale: "fa", value: values.faName},
      ],
    }
    const formData = new FormData();
    forEach(newValues, (value, key) => {
      if (key === 'logoUrl') {
        formData.append("image", dataURItoBlob(value));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    });
    try {
      await Api.invoke(
        'PUT',
        `/v2/admin-ui/pairs/${pair.name}`,
        formData,
      );
      afterSubmit();
      NotificationManager.success(t('Pair {{name}} updated successfully', {name: pair.name}), t('Message'));
      ModalManager.close({
        isOpen: false
      });
    } catch (e) {
      if (e.fieldError) {
        return e.fieldError;
      }
      throw e;
    }
  };

  render() {
    const {t, pair} = this.props;
    return(
      <FinalForm
        initialValues={{
          enName: pair && pair.labels.length ? find(pair.labels, {locale: "en"}).value : undefined,
          faName: pair && pair.labels.length ? find(pair.labels, {locale: "fa"}).value : undefined,
          tradeAmountDecimals: pair ? String(pair.tradeAmountDecimals) : "6",
          priceDecimals: pair ? String(pair.priceDecimals) : "0",
          active: pair ? pair.active : false,
          logoUrl: pair ? pair.logoUrl : undefined,
        }}
        onSubmit={pair ? this.updatePair : this.addPair}
        render={({handleSubmit, submitting, hasValidationErrors, values, form, initialValues}) => {
          return (
            <Form onSubmit={handleSubmit}>
              {!pair &&
                <Row className='mb-3'>
                  <Col>
                    <TextField item={{
                      name: 'baseToken',
                      i18n: t('Base Token'),
                      type: 'text',
                      required: true,
                    }}/>
                  </Col>
                  <Col>
                    <TextField item={{
                      name: 'quoteToken',
                      i18n: t('Quote Token'),
                      type: 'text',
                      required: true,
                    }}/>
                  </Col>
                </Row>
              }
              <Row className='mb-3'>
                <Col>
                  <TextField item={{
                    name: 'enName',
                    i18n: t('English Name'),
                    type: 'text',
                    required: true,
                  }}/>
                </Col>
                <Col>
                  <TextField item={{
                    name: 'faName',
                    i18n: t('Farsi Name'),
                    type: 'text',
                    required: true,
                  }}/>
                </Col>
              </Row>
              <Row className='mb-4'>
                <Col>
                  <Field name="tradeAmountDecimals" validate={composeValidators(required, mustBeNumber, mustBeInteger)}>
                    {({input}) => (
                      <div>
                        <span>{t('Trade Amount Decimals')}<span className="required">*</span></span>
                        <Input
                          {...input}
                          value={numberWithCommas(input.value)}
                          onChange={e => {input.onChange(stringToNumber(e.target.value));}}
                        />
                        <FieldError name="tradeAmountDecimals"/>
                      </div>
                    )}
                  </Field>
                </Col>
                <Col>
                  <Field name="priceDecimals" validate={composeValidators(required, mustBeNumber, mustBeInteger)}>
                    {({input}) => (
                      <div>
                        <span>{t('Price Decimals')}<span className="required">*</span></span>
                        <Input
                          {...input}
                          value={numberWithCommas(input.value)}
                          onChange={e => {input.onChange(stringToNumber(e.target.value));}}
                        />
                        <FieldError name="priceDecimals"/>
                      </div>
                    )}
                  </Field>
                </Col>
              </Row>
              <Row className='align-items-center mb-4'>
                <Switch
                  className="mx-3 ltr"
                  onChange={enabled => {form.change('active', enabled);}}
                  checked={values.active}
                  height={24}
                  width={45}
                />
                <span className={`mx-2 ${values.active ? 'text-success' : 'text-danger'}`}>
                  {t('Status')} {values.active ? t('Enabled') : t('Disabled')}
                </span>
              </Row>
              <Row className='align-items-center'>
                <Col>
                  <FileField
                    item={{
                      name: "logoUrl",
                      i18n: t("logo"),
                      path: values.logoUrl,
                      accept: "image/svg+xml",
                      previewSize: "60px",
                    }}
                  />
                </Col>
              </Row>
              <Row className='mt-5'>
                <Col>
                  <LoaderButton
                    color="success"
                    className="text-center"
                    loading={submitting}
                    onClick={handleSubmit}
                    style={{minWidth: '100px'}}
                    disabled={hasValidationErrors || submitting || isEqual(values, initialValues)}>
                    {pair ? t('Update') : t('Submit')}
                  </LoaderButton>
                  <Button
                    color="grey"
                    onClick={() =>{
                      ModalManager.close({
                        isOpen: false
                      });
                    }}
                    className="text-center mx-2"
                    style={{minWidth: '100px'}}>
                    {t('Close')}
                  </Button>
                </Col>
              </Row>
            </Form>
          );
        }}
      />
    );
  }
}

export default withTranslation()(PairManageForm);
